import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Coins, History, Plus, Minus, Gift, CreditCard, TrendingUp } from 'lucide-react'
import { useRraasiCoinStore, CoinTransaction } from '../stores/rraasiCoinStore'

interface RraasiCoinDisplayProps {
  showHistory?: boolean
  compact?: boolean
}

const RraasiCoinDisplay: React.FC<RraasiCoinDisplayProps> = ({ 
  showHistory = false, 
  compact = false 
}) => {
  const { balance, transactions, addCoins } = useRraasiCoinStore()
  const [isHistoryOpen, setIsHistoryOpen] = useState(showHistory)

  const getTransactionIcon = (type: CoinTransaction['type']) => {
    switch (type) {
      case 'earned':
        return <TrendingUp className="w-4 h-4 text-green-500" />
      case 'spent':
        return <Minus className="w-4 h-4 text-red-500" />
      case 'bonus':
        return <Gift className="w-4 h-4 text-yellow-500" />
      case 'subscription':
        return <CreditCard className="w-4 h-4 text-blue-500" />
      default:
        return <Coins className="w-4 h-4 text-gray-500" />
    }
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date))
  }

  const getTransactionColor = (type: CoinTransaction['type']) => {
    switch (type) {
      case 'earned':
      case 'bonus':
      case 'subscription':
        return 'text-green-600'
      case 'spent':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-2 rounded-full">
        <Coins className="w-4 h-4" />
        <span className="font-semibold">{balance.total}</span>
        <span className="text-sm opacity-90">RRAASI</span>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-full">
              <Coins className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">RRAASI Coins</h3>
              <p className="text-sm opacity-90">Your cosmic currency</p>
            </div>
          </div>
          <button
            onClick={() => setIsHistoryOpen(!isHistoryOpen)}
            className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
          >
            <History className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Balance Display */}
      <div className="p-4">
        <div className="text-center mb-4">
          <div className="text-3xl font-bold text-gray-800 mb-1">
            {balance.total.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">Available Coins</div>
        </div>

        {/* Balance Breakdown */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-green-600">
              +{balance.earned + balance.bonus + balance.subscription}
            </div>
            <div className="text-xs text-gray-500">Earned</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-red-600">
              -{balance.spent}
            </div>
            <div className="text-xs text-gray-500">Spent</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-blue-600">
              {balance.subscription}
            </div>
            <div className="text-xs text-gray-500">Subscription</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => addCoins(5, 'bonus', 'Daily login bonus!', 'daily-login')}
            className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Daily Bonus
          </button>
          <button
            onClick={() => addCoins(25, 'bonus', 'Referral bonus!', 'referral')}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Refer Friend
          </button>
        </div>
      </div>

      {/* Transaction History */}
      <AnimatePresence>
        {isHistoryOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-gray-200"
          >
            <div className="p-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Transaction History
              </h4>
              
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {transactions.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">
                    No transactions yet
                  </div>
                ) : (
                  transactions.map((transaction) => (
                    <motion.div
                      key={transaction.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-shrink-0">
                        {getTransactionIcon(transaction.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">
                          {transaction.description}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(transaction.timestamp)}
                        </div>
                      </div>
                      
                      <div className={`font-semibold ${getTransactionColor(transaction.type)}`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default RraasiCoinDisplay
